import { AddIcon, CheckCircleIcon, InfoOutlineIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  IconButton,
  Link,
  Menu, MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  SkeletonText,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import AppTableWithPagination from "components/AppTableWithPagination";
import ConfirmationModal from "components/genericForms/ConfirmationModal";
import SidebarWithHeader from "components/SidebarWithHeader";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { CiCreditCard1 } from "react-icons/ci";
import { FiMoreVertical } from "react-icons/fi";
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  useActivateSubscriptionMutation,
  useCancelSubscriptionMutation,
  useCheckPaymentMethodQuery,
  useDetachPaymentMethodMutation,
  useGetCustomerQuery,
  useGetSubscriptionByIdQuery,
  useGetSubscriptionsQuery,
  useLazyGetSubscriptionByIdQuery,
  useLazyGetSubscriptionsQuery,
  usePaymentDueMutation,
  useResumeSubscriptionMutation,
  useUpdatePaymentMethodMutation
} from "store/Subscription/SubscriptionApi";

const HIDE_MODAL_KEY = "hideAddPaymentModal";
const HIDE_MODAL_DATE_KEY = "hideAddPaymentModalDate";

function Subscription() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const inputBackground = useColorModeValue('none', '#1e1530');
  const modalInputBg = useColorModeValue('#fff', '#1e1530');
  const LinkColor = useColorModeValue('#421e66', '#b5b5cd');
  const bgColor = useColorModeValue("background.light", "background.dark");
  const darkPurple = useColorModeValue("#1b1845", "#8671cc");
  const borderColor = useColorModeValue('transparent', '#FF60D5')
  const boxBorderColor = useColorModeValue("gray.200", "purple.800");
  const textColor = useColorModeValue("mediumPurple.500", "mediumPurple.600");
  const [loadingDefaultId, setLoadingDefaultId] = useState(null);
  const [isPollingSubscriptions, setIsPollingSubscriptions] = useState(false);
  const [isWaitingActive, setIsWaitingActive] = useState(false);
  const [isPlanRefreshLoading, setIsPlanRefreshLoading] = useState(false);

  const {
    isOpen: isTrialSuccessOpen,
    onOpen: onTrialSuccessOpen,
    onClose: onTrialSuccessClose
  } = useDisclosure();

  const {
    isOpen: isAddPaymentModalOpen,
    onOpen: onAddPaymentModalOpen,
    onClose: onAddPaymentModalClose,
  } = useDisclosure(); 

  const {
    isOpen: isUpdatePaymentModalOpen,
    onOpen: onUpdatePaymentModalOpen,
    onClose: onUpdatePaymentModalClose,
  } = useDisclosure(); 
  
  const {
    isOpen: isActivateConfirmOpen,
    onOpen: onActivateConfirmOpen,
    onClose: onActivateConfirmClose,
  } = useDisclosure();

  const {
    isOpen: isCancelConfirmOpen,
    onOpen: onCancelConfirmOpen,
    onClose: onCancelConfirmClose,
  } = useDisclosure();
  
  // Lists & customer
  const { data: subscriptionList, isLoading: isSubscriptionListLoading, isFetching: isFetchingSubscriptionList, refetch: refetchSubscriptionList } = useGetSubscriptionsQuery();
  const { data: customer, isLoading: isGettingCustomer, refetch: refetchCustomer } = useGetCustomerQuery();
  const customerId = customer && customer?.id;
  const isCustomerReady = !!customerId && !isGettingCustomer;
  const defaultPaymentMethod = customer && customer?.defaultPaymentMethod;

  // Derive the "current" subscription ID from the list (non-canceled)
  const listCurrent = useMemo(
      () => (subscriptionList?.data?.length ? subscriptionList.data.find(sub => sub.status !== 'canceled') || null : null),
      [subscriptionList]
  );
  const hasAnySubscription = Array.isArray(subscriptionList?.data) && subscriptionList.data.length > 0;
  const currentSubscriptionId = listCurrent?.subscriptionId || null;
 
  const subscriptionIdToFetch = listCurrent?._id || null;

  // Fetch the actual subscription by ID (source of truth for UI)
  const {
    data: subscriptionById,
    isLoading: isSubscriptionLoading,
    isFetching: isFetchingSubscription,
    refetch: refetchSubscription,
  } = useGetSubscriptionByIdQuery(
    subscriptionIdToFetch ? subscriptionIdToFetch : skipToken
  );
  
  const currentSubscription = subscriptionById ?? null;

  // Status/time fields from the fetched-by-id subscription
  const subscriptionStatus = currentSubscription?.status;
  const trialEnd = Number(currentSubscription?.trialEnd);
  const currentPeriodEnd = Number(currentSubscription?.currentPeriodEnd);
  const cancelAt = Number(currentSubscription?.cancelAt);
  const isProPlan = (currentSubscription?.planRef?.name || "").toLowerCase() === "pro";


  // Payments
  const { data: checkPayments, isLoading:isCheckingPaymentLoading, refetch: refetchPayments } = useCheckPaymentMethodQuery(customerId, {skip:!customerId});
  const [detachPaymentMethod, { isLoading: isDetachingPayment }] = useDetachPaymentMethodMutation();
  const [updatePaymentMethod, { isLoading: isUpdatingPayment }] = useUpdatePaymentMethodMutation();
  const [triggerGetSubs] = useLazyGetSubscriptionsQuery();
  const [triggerGetSubById] = useLazyGetSubscriptionByIdQuery();
  
  // Plan actions
  const [activateSubscription, { isLoading: isUpgrading }] = useActivateSubscriptionMutation();
  const [cancelSubscription, { isLoading: isCanceling }] = useCancelSubscriptionMutation();
  const [paymentDue, { isLoading: isPaymentPaying }] = usePaymentDueMutation();
  const [resumeSubscription, { isLoading: isResuming }] = useResumeSubscriptionMutation();

  const hasPaymentMethod = checkPayments?.length > 0;

  useEffect(() => {
    if (!isCustomerReady) return;
    if (!(location.state && location.state.refreshSubs)) return;
  
    const rawExpected = location.state?.expectPlan || sessionStorage.getItem("expectedPlanAfterUpgrade");
  
    // If we don't expect a plan change, skip polling cleanup and just clear state
    if (!rawExpected) {
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
  
    const expected = rawExpected.toLowerCase();
    let tries = 0;
    let timerId;
    let cancelled = false;
  
    setIsPlanRefreshLoading(true);
  
    const attempt = () => {
      if (cancelled) return;
  
      const planNow = (currentSubscription?.planRef?.name || "").toLowerCase();
      if (planNow === expected || tries >= 3) {
        setIsPlanRefreshLoading(false);
        sessionStorage.removeItem("expectedPlanAfterUpgrade");
        navigate(location.pathname, { replace: true, state: {} });
        return;
      }
  
      tries += 1;
      refetchSubscriptionList();
      refetchSubscription();
      timerId = setTimeout(attempt, 1500);
    };
  
    attempt();
  
    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
      setIsPlanRefreshLoading(false);
    };
  }, [
    isCustomerReady,
    location.state,
    currentSubscription?.planRef?.name,
    refetchSubscriptionList,
    refetchSubscription,
    navigate,
  ]);

  useEffect(() => {
    if (!isCustomerReady) return;
  
    let tries = 0;
    let intervalId;
  
    const hasAny = Array.isArray(subscriptionList?.data) && subscriptionList.data.length > 0;
  
    if (!hasAny) {
      setIsPollingSubscriptions(true);
      intervalId = setInterval(() => {
        if (tries < 3) {
          tries += 1;
          refetchSubscriptionList();
        } else {
          clearInterval(intervalId);
          setIsPollingSubscriptions(false);
        }
      }, 3000);
    } else {
      setIsPollingSubscriptions(false);
    }
  
    return () => {
      if (intervalId) clearInterval(intervalId);
      setIsPollingSubscriptions(false);
    };
  }, [isCustomerReady, subscriptionList?.data?.length, refetchSubscriptionList]);
  
  
  useEffect(() => {
    if (currentSubscription?.status === "trialing"){
      const storedDate = localStorage.getItem(HIDE_MODAL_DATE_KEY);
      const today = new Date().toISOString().split("T")[0];
      if (storedDate && storedDate !== today) {
        localStorage.removeItem(HIDE_MODAL_KEY);
        localStorage.removeItem(HIDE_MODAL_DATE_KEY);
      }
      const isHidden = localStorage.getItem(HIDE_MODAL_KEY);
      if (!isHidden) {
        onAddPaymentModalOpen();
      }
    } 
  }, [currentSubscription]);
  
  const pollUntilActive = () => {
    const POLL_INTERVAL_MS = 3000;
    const MAX_TRIES = 3;

    let tries = 0;
  
    const intervalId = setInterval(async () => {
      tries += 1;
  
      const [subsPayload, byIdPayload] = await Promise.all([
        triggerGetSubs().unwrap(),
        subscriptionIdToFetch
          ? triggerGetSubById(subscriptionIdToFetch).unwrap()
          : Promise.resolve(null),
      ]);
  
      const status =
        byIdPayload?.status ??
        subsPayload?.data?.find(s => s.status !== 'canceled')?.status ??
        null;
  
      if (status !== 'trialing') {
        clearInterval(intervalId);
        setIsWaitingActive(false); // <- stop spinner when active
  
        // refresh cache so rest of UI is in sync
        refetchSubscriptionList();
        refetchSubscription();
  
        onTrialSuccessOpen();
      } else if (tries >= MAX_TRIES) {
        clearInterval(intervalId);
        setIsWaitingActive(false); // <- stop spinner if we give up
      }
    }, POLL_INTERVAL_MS);
  
    return () => clearInterval(intervalId);
  };
  
  const columns = useMemo(
    () => [
      {
        Header: 'subscription ID',
        accessor: 'subscriptionId',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (<Text fontSize={'14px'}>{original.subscriptionId}</Text>)
      },
      {
        Header: 'Plan',
        accessor: '',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (<Text fontSize={'14px'}>{original.planRef.name}</Text>)
      },
      {
        Header: "Status",
        accessor: "status",
        Filter: "",
        filter: "",
        Cell: ({ row: { original } }) => {
          let badgeColor = 'gray';
          if (original?.status === 'trialing') {
            badgeColor = 'yellow';
          } else if (original?.status === 'canceled') {
            badgeColor = 'red';
          } else if (original?.status === 'active') {
            badgeColor = 'green';
          } else if (original?.status === 'past_due') {
            badgeColor = 'blue';
          }
          return (
            <Badge fontSize={"10px"} mr={1} colorScheme={badgeColor}>
              {original?.status}
            </Badge>
          );
        },
      },    
      {
        Header: 'Start Date',
        accessor: 'startDate',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (
          <Text>{moment(original.currentPeriodStart * 1000).format("DD-MM-YYYY hh:mm A")}</Text>
        )
      },
      {
        Header: 'End Date',
        accessor: 'endDate',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (
          <Text>{moment(original.currentPeriodEnd * 1000).format("DD-MM-YYYY hh:mm A")}</Text>
        )
      },      
    ],
    []
  );

  const handleDetachPayment = (cardId) => {
    detachPaymentMethod({ cardId })
      .unwrap()
      .then(() => {
        refetchSubscriptionList();
        refetchSubscription();
        setTimeout(() => {
          refetchSubscriptionList();
          refetchSubscription();
        }, 1000);
        refetchPayments();
        toast({
          position: "top-right",
          title: "Removed",
          description: "Payment method detached successfully.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      })
      .catch((error) => {
        toast({
          position: "top-right",
          title: "Error",
          description: error?.data?.message || "Failed to detach payment method.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      });
  };
  
  const handleActivateSubscription = () => {
    if (!currentSubscriptionId) return;
    setIsWaitingActive(true);
    activateSubscription({ subscriptionId: currentSubscriptionId })
      .unwrap()
      .then(() => {
        pollUntilActive();
      })
      .catch(error => {
        setIsWaitingActive(false);
        toast({
          position: "top-right",
          title: "Something went wrong",
          description: error?.data?.message || error?.message || "Please try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      });
  };

  const handleCancelSubscription = () => {
    if (!currentSubscriptionId) return;
    cancelSubscription({ subscriptionId: currentSubscriptionId })
      .unwrap()
      .then(response => {
        refetchSubscriptionList();
        refetchSubscription();
        toast({
          position: "top-right",
          title: "Canceled",
          description: response?.data?.message || response?.message || "Subscription canceled succesfully.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      })
      .catch(error => {
        toast({
          position: "top-right",
          title: "Failed to cancel subscription",
          description: error?.data?.message || error?.message || "Please try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      });
  };
  
  const handleSetDefaultMethod = (methodId) => {
    setLoadingDefaultId(methodId);
    updatePaymentMethod({
      subscriptionId: currentSubscriptionId,
      payload: { default_payment_method: methodId },
    })
      .unwrap()
      .then((response) => {
        refetchCustomer();
        refetchSubscription();
        setTimeout(() => {
          refetchCustomer();
          refetchSubscription();
        }, 1000);
        refetchPayments();
        toast({
          position: 'top-right',
          title: 'Updated',
          description: response?.data?.message || response?.message,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      })
      .catch((error) => {
        toast({
          position: 'top-right',
          title: 'Something went wrong',
          description:
            error?.data?.message ||
            error?.message ||
            'Please try again.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        })
      })
      .finally(() => {
        setLoadingDefaultId(null);           
      });
  };

  const handlePayDue = () => {
    if (!currentSubscriptionId) return;
    paymentDue({ subscriptionId: currentSubscriptionId })
      .unwrap()
      .then(response => {
        window.open(response?.data?.paymetUrl, '_blank');
      })
      .catch(error => {
        toast({
          position: "top-right",
          title: "Something went wrong",
          description: error?.data?.message || error?.message || "Please try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      });
  };

  const handleResumeSubscription = () => {
    if (!currentSubscriptionId) return;
    resumeSubscription({ subscriptionId: currentSubscriptionId })
      .unwrap()
      .then(response => {
        refetchSubscriptionList();
        refetchSubscription();
        setTimeout(() => {
          refetchSubscriptionList();
          refetchSubscription();
        }, 1000);
        toast({
          position: 'top-right',
          title: 'Resume Successfull',
          description: response?.data?.message || response?.message,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      })
      .catch(error => {
        toast({
          position: "top-right",
          title: "Something went wrong",
          description: error?.data?.message || error?.message || "Please try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      });
  };

  const defaultPayment = checkPayments?.find(
    (method) => method.id === defaultPaymentMethod
  );
  
  const handleSkip = () => {
    const today = new Date().toISOString().split("T")[0];
    localStorage.setItem(HIDE_MODAL_KEY, "true");
    localStorage.setItem(HIDE_MODAL_DATE_KEY, today);
    onAddPaymentModalClose();
  };

  // If trialing, calculate how many days left  
  const daysLeft = trialEnd
    ? moment.unix(trialEnd).diff(moment(), 'days')
    : null;

  // Text to show for trial
  const endingText = daysLeft !== null
    ? daysLeft > 0
      ? `Ending in ${daysLeft} day${daysLeft > 1 ? 's' : ''}`
      : daysLeft === 0
        ? 'Ends today'
        : 'Expired'
    : '';

  const formattedReason = currentSubscription?.cancellation_details?.reason
    ? currentSubscription.cancellation_details.reason
        .replace(/_/g, ' ')
        .replace(/^\w/, c => c.toUpperCase())
    : null;

  const isBootLoading = isSubscriptionListLoading || isGettingCustomer || isSubscriptionLoading || isFetchingSubscriptionList || isPollingSubscriptions || isPlanRefreshLoading;

  return (
    <>
      {/* Subscription Modal for Free Plan */}
      <Modal isOpen={isTrialSuccessOpen} onClose={onTrialSuccessClose} isCentered size="lg" motionPreset="scale">
        <ModalOverlay />
        <ModalContent bg={bgColor} border="1px solid" borderColor={borderColor} borderRadius="20px">
          <ModalCloseButton />
          <ModalBody py={10} px={6}>
            <Flex alignItems="center" justifyContent="center" direction="column" gap={2}>
              <Text fontSize="4xl">🤘</Text>
              <Text fontSize="2xl" fontWeight="bold">
                You're in!
              </Text>
              <Text color={darkPurple} mb="10px" fontSize="lg">
                Your subscription is live.
              </Text>
              <Button colorScheme="brand" width="60%" onClick={() => navigate('/launchpad')}>
                Time to Hustle
              </Button>
              <Text fontSize="sm" color="gray.500" mt={2} textAlign="center">
                You can update your payment details or cancel your subscription anytime from here.
              </Text>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Added payment Modal */}
      <Modal isOpen={isAddPaymentModalOpen} onClose={handleSkip} isCentered size={'lg'}>
        <ModalOverlay />
        <ModalContent bg={bgColor} border="1px solid" borderColor={borderColor} borderRadius="20px">
          <ModalCloseButton />
          <ModalBody py={10} px={6}>
            <Flex alignItems={'center'} justifyContent={'center'} direction={'column'} gap={2}>
              <Text fontSize="4xl">💪</Text>
              <Text fontSize="2xl" fontWeight="bold">
                Keep Hustlin’
              </Text>
              <Text color={darkPurple} mb={'10px'} fontSize="lg">Add payment details now to stay live post-trial.</Text>
              <Button 
                colorScheme="brand"  
                onClick={() => {
                      localStorage.setItem("allowPricingEntry", "true");
                      navigate(`/pricing/adding-payment/${customerId}`);
                  }} 
                  width={'70%'}
                >
                Add Payment
              </Button>
              <Button
                variant="link"
                color="purple.300"
                _hover={{ textDecoration: 'underline' }}
                onClick={() => handleSkip()}
              >
                Skip for Now
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Update payment Modal */}
      <Modal isOpen={isUpdatePaymentModalOpen} onClose={onUpdatePaymentModalClose} isCentered size={'2xl'} key={(defaultPaymentMethod) ?? 'noDefault'}>
        <ModalOverlay />
        <ModalContent bg={bgColor} border="1px solid" borderColor={borderColor}>
          <ModalHeader p={'12px'}>
            <Flex direction={'column'}>
              <Text fontWeight="semibold" fontSize={'20px'}>Payment methods</Text>
              <Text fontSize={'12px'} fontWeight={'normal'}>Choose how you want to pay</Text>
            </Flex>
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody p={3}>
              <Flex direction="column" gap={6}>
                {/* Scrollable section */}
                <Box maxH="300px" overflowY="auto" pr={2}>
                  {hasPaymentMethod ? (
                    <Box width="100%" p={2}>
                        {checkPayments?.map((method) => {
                          const isDefault = method.id === defaultPaymentMethod;
                          const brandRaw = method?.card?.display_brand || method?.card?.brand || "";
                          const brand = brandRaw.charAt(0).toUpperCase() + brandRaw.slice(1);
                          const last4 = method?.card?.last4;
                          const expMonth = String(method?.card?.exp_month ?? "").padStart(2, "0");
                          const expYear = method?.card?.exp_year ?? "";
                          const expiresText = `Expires ${expMonth}/${expYear}`;

                          return (
                            <Box key={method.id} position="relative">
                              {isDefault && (
                                <Box
                                  position="absolute"
                                  top="-10px"
                                  right="-8px"
                                  bg={modalInputBg}
                                  borderRadius="full"
                                  border="1px solid"
                                  borderColor={modalInputBg}
                                  zIndex="2"
                                >
                                  <CheckCircleIcon boxSize={5} color="green.500" />
                                </Box>
                              )}

                              <Flex
                                border="1px solid"
                                borderColor={isDefault ? "green.500" : "gray.200"}
                                borderRadius="md"
                                p={2}
                                mb={3}
                                align="center"
                                justify="space-between"
                                gap={3}
                              >
                                {/* Left: brand •••• last4 */}
                                <Flex align="center" gap={3} minW={0} flex="1">
                                  <Icon as={CiCreditCard1} boxSize={5} />
                                  <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                                    {brand}
                                    <Box as="span" letterSpacing="0.2em" fontSize="md" pl={1}>••••</Box>
                                    {last4}
                                  </Text>
                                </Flex>

                                {/* Right: expires + menu */}
                                <Flex align="center" gap={2}>
                                  <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                                    {expiresText}
                                  </Text>

                                  <Menu placement="bottom-end" strategy="fixed" isLazy>
                                    <MenuButton
                                      as={IconButton}
                                      aria-label="More options"
                                      icon={<FiMoreVertical />}
                                      size="sm"
                                      variant="ghost"
                                      isDisabled={isUpdatingPayment}
                                      isLoading={loadingDefaultId === method.id }
                                    />
                                      <MenuList zIndex="modal" fontSize={'14px'} minW="160px" w="auto">
                                        {!isDefault && (
                                          <MenuItem onClick={() => handleSetDefaultMethod(method.id)}>
                                            Make default
                                          </MenuItem>
                                        )}

                                        {isDefault ? (
                                          <Tooltip label="You can't delete this payment method because you have an active plan that uses it." fontSize="12px">
                                            <span>
                                              <MenuItem isDisabled>
                                                Delete
                                              </MenuItem>
                                            </span>
                                          </Tooltip>
                                        ) : (
                                          <MenuItem
                                            color={'red.500'}
                                            onClick={() => handleDetachPayment(method.id)}
                                            isDisabled={isDetachingPayment}
                                          >
                                            Delete
                                          </MenuItem>
                                        )}
                                      </MenuList>
                                  </Menu>
                                </Flex>
                              </Flex>
                            </Box>
                          );
                        })}
                    </Box>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      No saved payment methods found.
                    </Text>
                  )}
                </Box>

                <Button
                  leftIcon={<AddIcon boxSize={3} />}
                  size={'sm'}
                  width="200px"
                  variant="outline"
                  colorScheme="brand"
                  onClick={() => {
                    localStorage.setItem("allowPricingEntry", "true");
                    navigate(`/pricing/adding-payment/${customerId}`);
                  }}
                >
                  Add payment method
                </Button>
              </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      <ConfirmationModal
        isOpen={isActivateConfirmOpen}
        onClose={() => {
          onActivateConfirmClose();
        }}
        onConfirm={() => {
          handleActivateSubscription();
          onActivateConfirmClose();
        }}
        emoji="🚀"
        title="Time to Hustle!"
        body={`Your subscription will start immediately, and your payment method will be charged ${currentSubscription?.planRef?.name === 'Starter' ? '$9.99' : currentSubscription?.planRef?.name === 'Pro' ? '$18.99' : ''}.`} 
        confirmLabel="Activate Plan"
        cancelLabel="Cancel"
      />

      <ConfirmationModal
        isOpen={isCancelConfirmOpen}
        onClose={onCancelConfirmClose}
        onConfirm={() => {
          handleCancelSubscription();
          onCancelConfirmClose();
        }}
        emoji="😢"
        title="Hate to see you go"
        body={`Cancelling means no more Hustlin’ with us after ${moment.unix(currentSubscription?.currentPeriodEnd).format("MMM D, YYYY")}. You’ll still enjoy your perks until then.`}
        confirmLabel="Cancel Anyway"
        cancelLabel="Stay Subscribed"
      />

      <SidebarWithHeader>
        <Heading fontSize="3xl" color={textColor} mb={2}>
            Subscription
        </Heading>

        {isBootLoading ? (
          <Flex
            direction="column"
            gap={2}
            alignItems="center"
            justifyContent="center"
            h="60vh"
          >
            <Spinner />
              <Text>
              Loading …
              </Text>
          </Flex>
        ) : hasAnySubscription && currentSubscription ? (
          <Box>
            <Flex mb={4} gap={4} direction={{ base: 'column', md: 'row' }}>
              {/* Subscription Plan Box */}
              <Flex
                bg={inputBackground}
                p={4}
                borderRadius="lg"
                border="1px solid"
                borderColor={boxBorderColor}
                flex={1}
                justifyContent={'space-between'}
              >
                <Flex direction="column" gap={4}>
                  <Flex direction="column">
                    <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="semibold">
                      {`${currentSubscription?.planRef?.name} plan`}
                    </Text>
                    <Text fontSize="sm">
                      {subscriptionStatus === 'trialing'
                        ? 'Free Trial'
                        : subscriptionStatus?.charAt(0).toUpperCase() + subscriptionStatus?.slice(1)}
                    </Text>
                  </Flex>

                <Text fontSize="sm">
                  {currentSubscription?.cancelAtPeriodEnd && cancelAt ? (
                    <Flex align="center" gap={1}>
                      <>
                        Your plan will be canceled at{' '}
                        <Text as="span" fontWeight="bold">
                          {moment.unix(cancelAt).format('D MMM YYYY')}
                        </Text>
                      </>
                      {formattedReason && (
                        <Tooltip
                          label={`Reason: ${formattedReason}`}
                          fontSize="xs"
                          placement="top"
                          hasArrow
                        >
                          <InfoOutlineIcon boxSize={3.5} />
                        </Tooltip>
                      )}
                    </Flex>
                  ) : subscriptionStatus === 'trialing' ? (
                    <>
                      Ending in{' '}
                      <Text as="span" fontWeight="bold">
                        {endingText.replace('Ending in ', '')}
                      </Text>
                    </>
                  ) : (
                    <>
                      Your plan renews{' '}
                      <Text as="span" fontWeight="bold">
                        {moment.unix(currentPeriodEnd).format('D MMM YYYY')}
                      </Text>
                    </>
                  )}
                </Text>

                </Flex>

                <Flex>
                  {subscriptionStatus === 'past_due' ? (
                    <Button
                      colorScheme="brand"
                      size="sm"
                      px={5}
                      py={2}
                      borderRadius="md"
                      onClick={handlePayDue}
                      isLoading={isPaymentPaying}
                    >
                      Pay due amount
                    </Button>
                  ) : subscriptionStatus === 'paused' ? (
                    <Button
                      colorScheme="brand"
                      size="sm"
                      px={5}
                      py={2}
                      borderRadius="md"
                      onClick={handleResumeSubscription}
                      isLoading={isResuming}
                    >
                      Resume Subscription
                    </Button>
                  ) : subscriptionStatus === 'trialing' && !currentSubscription?.cancelAtPeriodEnd ? (
                    <Tooltip
                      label="Set up payment to enable your plan"
                      isDisabled={!!defaultPaymentMethod}
                      hasArrow
                      placement="top"
                      fontSize="12px"
                      p={2}
                    >
                      <Button
                        colorScheme="brand"
                        size="sm"
                        px={5}
                        py={2}
                        borderRadius="md"
                        isLoading={isWaitingActive}
                        onClick={() => {
                          onActivateConfirmOpen();
                        }}
                        isDisabled={!defaultPaymentMethod || isWaitingActive}
                      >
                        Activate your plan
                      </Button>
                    </Tooltip>
                  ) : subscriptionStatus === 'active' && !currentSubscription?.cancelAtPeriodEnd && !isProPlan ? (
                    <Link color={LinkColor} onClick={() => navigate('/pricing')}>
                      Upgrade plan
                    </Link>
                  ) : null}
                </Flex>
              </Flex>

              {/* Payment Method Box */}
              <Flex
                bg={inputBackground}
                p={4}
                borderRadius="lg"
                border="1px solid"
                borderColor={boxBorderColor}
                flex={1}
                justify="space-between"
              >
                <Flex direction="column" gap={2}>
                  <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="semibold">
                    Payment
                  </Text>

                  {isCheckingPaymentLoading ? (
                    <Flex>
                      <SkeletonText width="100%" noOfLines={2} skeletonHeight={1.5} />
                    </Flex>
                  ) : defaultPayment ? (
                    // Default payment exists: show masked card
                    <Flex align="center" gap={2} wrap="wrap">
                      <Icon as={CiCreditCard1} boxSize={5} />
                      <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="medium">
                        {`${defaultPayment?.card?.display_brand?.charAt(0).toUpperCase()}${defaultPayment?.card?.display_brand?.slice(1)} `}
                        <Box as="span" letterSpacing="0.2em" fontSize="md" px={1}>
                          ••••
                        </Box>
                        {defaultPayment?.card?.last4}
                      </Text>
                    </Flex>
                  ) : hasPaymentMethod ? (
                    // Cards saved but no default selected
                    <Text fontSize="sm">
                      Payment method saved. Set a default to continue.
                    </Text>
                  ) : (
                    // No cards at all
                    <Text fontSize="sm">No payment added yet</Text>
                  )}
                </Flex>

                <Flex>
                  {(isBootLoading || isCheckingPaymentLoading) ? (
                    <Box minW="120px">
                      <Skeleton height="32px" borderRadius="md" />
                    </Box>
                  ) : hasPaymentMethod ? (
                    <Button
                      colorScheme="brand"
                      size="sm"
                      borderRadius="md"
                      onClick={() => {
                        // if there are cards, open manage to select default
                        onUpdatePaymentModalOpen();
                      }}
                    >
                      {defaultPayment ? "Manage" : "Set default"}
                    </Button>
                  ) : (
                    <Button
                      colorScheme="brand"
                      size="sm"
                      borderRadius="md"
                      onClick={() => {
                        localStorage.setItem("allowPricingEntry", "true");
                        navigate(`/pricing/adding-payment/${customerId}`);
                      }}
                    >
                      Add payment
                    </Button>
                  )}
                </Flex>

              </Flex>
            </Flex>

            {/* Invoices Section */}
            {currentSubscription?.invoices?.length > 0 ? (
              <Flex
                bg={inputBackground}
                p={4}
                borderRadius="lg"
                border="1px solid"
                borderColor={boxBorderColor}
                flex={1}
                direction="column"
                my={4}
              >
                <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="semibold" mb={2}>
                  Invoices
                </Text>

                <Box maxHeight="160px" overflowY="auto">
                  <Table
                    variant="unstyled"
                    size="sm"
                    sx={{ "th, td": { py: "6px", px: "8px" } }}
                  >
                    <Thead>
                      <Tr>
                        <Th>Date</Th>
                        <Th>Total</Th>
                        <Th>Status</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {[...currentSubscription.invoices]
                        .sort((a, b) => b.created - a.created)
                        .map((invoice) => {
                          const isDraft = invoice.status === "draft";
                          return (
                            <Tr key={invoice.id}>
                              <Td>
                                {new Date(invoice.created * 1000).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </Td>
                              <Td>{`$${(invoice.total / 100).toFixed(2)}`}</Td>
                              <Td>
                                {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                              </Td>
                              <Td>
                                <Tooltip label={isDraft ? "Draft" : ""} isDisabled={!isDraft}>
                                  <Button
                                    size="xs"
                                    colorScheme="gray"
                                    variant="outline"
                                    as="a"
                                    href={isDraft ? undefined : invoice.hostedInvoiceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    isDisabled={isDraft}
                                    pointerEvents={isDraft ? "none" : "auto"}
                                  >
                                    View
                                  </Button>
                                </Tooltip>
                              </Td>
                            </Tr>
                          );
                        })}
                    </Tbody>
                  </Table>
                </Box>
              </Flex>
            ) : null}

            <Flex
              bg={inputBackground}
              p={4}
              my={4}
              borderRadius="lg"
              border="1px solid"
              borderColor={boxBorderColor}
              flex={1}
              direction={'column'}
            >
              <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="semibold" mb={2}>
                Cancellation
              </Text>

              <Flex alignItems={'center'} justifyContent={'space-between'}>
                <Text fontSize={'sm'}>
                  {currentSubscription?.cancelAtPeriodEnd && currentSubscription?.canceledAt
                    ? `Your plan was canceled on ${moment.unix(Number(currentSubscription.canceledAt)).format("D MMM YYYY")}`
                    : 'Cancel plan'}
                </Text>

                {currentSubscription?.cancelAtPeriodEnd ? null : (
                  <Button
                    colorScheme="brand"
                    size="sm"
                    px={5}
                    py={2}
                    isDisabled={currentSubscription?.status === 'canceled'}
                    borderRadius="md"
                    onClick={onCancelConfirmOpen}
                    isLoading={isCanceling}
                  >
                    Cancel
                  </Button>
                )}
              </Flex>
            </Flex>

            {/* Table (shows just the current/non-canceled sub) */}
            {isSubscriptionListLoading || isGettingCustomer || isSubscriptionLoading ? (
              <Spinner mt="20px" />
            ) : (
              <AppTableWithPagination
                columns={columns}
                data={currentSubscription ? [currentSubscription] : []}
                paginationMeta={subscriptionList?.meta || {}}
                onPageChange={() => {}}
                onSearchInputChange={() => {}}
                searchEnabled={false}
                isFetchingSubscriptionList={isFetchingSubscriptionList || isFetchingSubscription}
                emptyMessage={"No active subscriptions. Choose a plan to get started!"}
              />
            )}
          </Box>
        ) : (
          <Text>
            No active subscriptions.{' '}
            <Link as={RouterLink} to="/pricing" fontWeight={'bold'} textDecoration="underline">
              Choose a plan
            </Link>{' '}
            to get started!
          </Text>
        )}
      </SidebarWithHeader>
    </>
  );
}

export default Subscription;
